
import { supabase } from "@/integrations/supabase/client";

// Define interface for the conversation history
export interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Call the smart API service to generate AI responses
 * 
 * @param query The user's question
 * @param history Previous conversation history
 * @returns An AI-generated response
 */
export async function callSmartApi(
  query: string,
  history: ConversationMessage[] = []
): Promise<string> {
  try {
    // If the query is empty, return a default message
    if (!query.trim()) {
      return "I'm here to help with your fundraising questions. What would you like to know?";
    }
    
    // Check if we should use the supabase function or fallback to local processing
    const { data, error } = await supabase.functions.invoke('generate-response', {
      body: { 
        query, 
        history: history.map(msg => ({
          role: msg.role,
          content: msg.content
        }))
      }
    });
    
    if (error) {
      console.error('Error calling Supabase function:', error);
      // Fallback to local response processing if the API call fails
      return generateFallbackResponse(query, history);
    }
    
    return data?.response || "I couldn't generate a response. Please try again.";
  } catch (error) {
    console.error('Error in callSmartApi:', error);
    return generateFallbackResponse(query, history);
  }
}

/**
 * Generate a fallback response when the API is unavailable
 * This is a simple implementation that handles common fundraising questions
 */
function generateFallbackResponse(
  query: string, 
  history: ConversationMessage[]
): string {
  const lowerQuery = query.toLowerCase();
  
  // Handle greeting
  if (
    lowerQuery.includes('hello') || 
    lowerQuery.includes('hi') || 
    lowerQuery.includes('hey')
  ) {
    return "Hello! I'm your fundraising advisor. How can I help you today?";
  }
  
  // Handle pitch deck questions
  if (lowerQuery.includes('pitch deck') || lowerQuery.includes('presentation')) {
    return "A great pitch deck typically includes 10-12 slides covering your problem statement, solution, market size, business model, traction, team, competition, and fundraising goals. Would you like advice on a specific part of your pitch deck?";
  }
  
  // Handle valuation questions
  if (
    lowerQuery.includes('valuation') || 
    lowerQuery.includes('worth') || 
    lowerQuery.includes('value')
  ) {
    return "Startup valuation depends on multiple factors including revenue, growth rate, market size, team experience, and comparable deals. Early-stage startups often use methods like the Berkus Method or Venture Capital Method. Would you like to learn more about a specific valuation approach?";
  }
  
  // Handle investor questions
  if (
    lowerQuery.includes('investor') || 
    lowerQuery.includes('vc') || 
    lowerQuery.includes('angel')
  ) {
    return "Finding the right investors involves researching those who typically invest in your industry, stage, and deal size. It's important to build a targeted list and craft personalized outreach. Would you like advice on approaching investors or creating an investor list?";
  }
  
  // Default response for when we can't categorize the query
  return "That's an interesting question about fundraising. To give you the most helpful answer, could you provide a bit more context about your startup's stage and specific fundraising goals?";
}

/**
 * Function to handle more advanced AI processing with enhanced context
 * This is used for premium features that require more sophisticated processing
 */
export async function processEnhancedQuery(
  query: string,
  context: Record<string, any>
): Promise<string> {
  try {
    // Call the enhanced processing endpoint
    const { data, error } = await supabase.functions.invoke('enhanced-response', {
      body: { 
        query,
        context
      }
    });
    
    if (error) {
      console.error('Error calling enhanced response function:', error);
      return callSmartApi(query); // Fallback to standard response
    }
    
    return data?.response || "I couldn't generate an enhanced response. Falling back to standard processing.";
  } catch (error) {
    console.error('Error in processEnhancedQuery:', error);
    return callSmartApi(query); // Fallback to standard response
  }
}
